import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../i18n";
import api from "../api";

// Site key effettiva: endpoint pubblico /api/public/config (l'admin può cambiarla
// senza rebuild); come valore iniziale si usa l'eventuale variabile di build.
let CURRENT = {
  enabled: Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY),
  site_key: process.env.REACT_APP_TURNSTILE_SITE_KEY || "",
};
let cfgPromise;

export function loadTurnstileConfig() {
  if (!cfgPromise) {
    cfgPromise = api.get("/config")
      .then(({ data }) => {
        CURRENT = { enabled: Boolean(data?.turnstile_enabled && data?.turnstile_site_key), site_key: data?.turnstile_site_key || "" };
        return CURRENT;
      })
      .catch(() => CURRENT);
  }
  return cfgPromise;
}

export function turnstileActive() {
  return CURRENT.enabled;
}

let scriptPromise;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Turnstile script failed to load"));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export default function Turnstile({ onToken }) {
  const container = useRef(null);
  const widgetId = useRef(null);
  const [cfg, setCfg] = useState(CURRENT.enabled ? CURRENT : null);
  const [failed, setFailed] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    let disposed = false;
    loadTurnstileConfig().then((c) => {
      if (disposed) return;
      if (!c.enabled || !c.site_key) { setCfg(null); onToken(null); return; }
      setCfg(c);
      loadTurnstile().then(() => {
        if (disposed || !container.current || !window.turnstile) return;
        window.turnstile.ready(() => {
          if (disposed || !container.current || !window.turnstile) return;
          widgetId.current = window.turnstile.render(container.current, {
            sitekey: c.site_key,
            theme: "auto",
            callback: (token) => { setFailed(false); onToken(token); },
            "expired-callback": () => { onToken(null); if (widgetId.current != null) window.turnstile?.reset(widgetId.current); },
            "error-callback": () => { setFailed(true); onToken(null); },
            "timeout-callback": () => { onToken(null); if (widgetId.current != null) window.turnstile?.reset(widgetId.current); },
          });
        });
      }).catch(() => { setFailed(true); onToken(null); });
    });
    return () => {
      disposed = true;
      if (widgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* already removed */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!cfg) return null;
  return (
    <div data-testid="turnstile-widget" className="flex flex-col items-start gap-1">
      <div ref={container} />
      {failed && <p data-testid="turnstile-error" className="text-xs text-red-600">{t("misc.captcha")}</p>}
    </div>
  );
}
